const STORAGE_KEY = "crm.status-tone-overrides";

const TONES = {
    sky: {
        row: "bg-sky-50/80 dark:bg-sky-950/25",
        surface: "bg-sky-50/70 border-sky-200/70 dark:bg-sky-950/20 dark:border-sky-800/45",
        badge: "bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-900/35 dark:text-sky-100 dark:border-sky-800/50",
    },
    cyan: {
        row: "bg-cyan-50/80 dark:bg-cyan-950/25",
        surface: "bg-cyan-50/70 border-cyan-200/70 dark:bg-cyan-950/20 dark:border-cyan-800/45",
        badge: "bg-cyan-100 text-cyan-800 border-cyan-200 dark:bg-cyan-900/35 dark:text-cyan-100 dark:border-cyan-800/50",
    },
    yellow: {
        row: "bg-yellow-50/80 dark:bg-yellow-950/25",
        surface: "bg-yellow-50/70 border-yellow-200/70 dark:bg-yellow-950/20 dark:border-yellow-800/45",
        badge: "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/35 dark:text-yellow-100 dark:border-yellow-800/50",
    },
    purple: {
        row: "bg-violet-50/80 dark:bg-violet-950/25",
        surface: "bg-violet-50/70 border-violet-200/70 dark:bg-violet-950/20 dark:border-violet-800/45",
        badge: "bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/35 dark:text-violet-100 dark:border-violet-800/50",
    },
    orange: {
        row: "bg-orange-50/80 dark:bg-orange-950/25",
        surface: "bg-orange-50/70 border-orange-200/70 dark:bg-orange-950/20 dark:border-orange-800/45",
        badge: "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/35 dark:text-orange-100 dark:border-orange-800/50",
    },
    amber: {
        row: "bg-amber-50/80 dark:bg-amber-950/25",
        surface: "bg-amber-50/70 border-amber-200/70 dark:bg-amber-950/20 dark:border-amber-800/45",
        badge: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/35 dark:text-amber-100 dark:border-amber-800/50",
    },
    green: {
        row: "bg-emerald-50/80 dark:bg-emerald-950/25",
        surface: "bg-emerald-50/70 border-emerald-200/70 dark:bg-emerald-950/20 dark:border-emerald-800/45",
        badge: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/35 dark:text-emerald-100 dark:border-emerald-800/50",
    },
    red: {
        row: "bg-rose-50/80 dark:bg-rose-950/25",
        surface: "bg-rose-50/70 border-rose-200/70 dark:bg-rose-950/20 dark:border-rose-800/45",
        badge: "bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/35 dark:text-rose-100 dark:border-rose-800/50",
    },
    gray: {
        row: "bg-slate-50/80 dark:bg-slate-900/35",
        surface: "bg-slate-50/70 border-slate-200/70 dark:bg-slate-900/30 dark:border-slate-700/45",
        badge: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800/70 dark:text-slate-100 dark:border-slate-700",
    },
    pink: {
        row: "bg-pink-50/80 dark:bg-pink-950/25",
        surface: "bg-pink-50/70 border-pink-200/70 dark:bg-pink-950/20 dark:border-pink-800/45",
        badge: "bg-pink-100 text-pink-800 border-pink-200 dark:bg-pink-900/35 dark:text-pink-100 dark:border-pink-800/50",
    },
};

const DEFAULT_TONES = {
    status: {
        new_lead: "sky",
        contacted: "cyan",
        follow_up: "yellow",
        meeting_scheduled: "purple",
        proposal_sent: "orange",
        negotiation: "amber",
        won: "green",
        lost: "red",
        on_hold: "gray",
        archived: "gray",
        overdue_follow_up: "red",
        pending: "yellow",
        in_progress: "sky",
        completed: "green",
        cancelled: "red",
        draft: "gray",
        sent: "sky",
        partially_paid: "amber",
        overdue: "red",
        paid: "green",
        active: "green",
        inactive: "gray",
        planning: "sky",
        valid: "green",
        risky: "amber",
        invalid: "red",
        unknown: "gray",
        crawled: "sky",
        failed: "red",
        skipped: "gray",
        todo: "yellow",
        blocked: "red",
        refunded: "gray",
    },
    source: {
        upwork: "sky",
        website: "cyan",
        referral: "green",
        other: "gray",
    },
    validation: {
        valid: "green",
        risky: "amber",
        invalid: "red",
        unknown: "gray",
    },
    priority: {
        high: "pink",
        medium: "yellow",
        low: "gray",
        urgent: "red",
    },
    activity: {
        lead: "sky",
        follow_up: "yellow",
        proposal: "orange",
        project: "green",
        client: "purple",
        user: "gray",
        discovery: "cyan",
    },
    task: {
        todo: "yellow",
        in_progress: "sky",
        done: "green",
        blocked: "red",
        cancelled: "gray",
    },
    payment: {
        paid: "green",
        partially_paid: "amber",
        pending_payment: "orange",
        overdue: "red",
        failed: "red",
        refunded: "gray",
    },
};

function normalizeKey(value) {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_")
        .replace(/__+/g, "_");
}

function readOverrides() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

function getTone(group, value) {
    const key = normalizeKey(value);
    const overrides = readOverrides();
    const overrideTone = overrides?.[group]?.[key];
    if (overrideTone && TONES[overrideTone]) return overrideTone;
    return DEFAULT_TONES[group]?.[key] || DEFAULT_TONES.status[key] || "gray";
}

function formatLabel(value) {
    const label = String(value || "").trim().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
    if (!label) return "—";
    return label
        .split(" ")
        .map((word) => word ? word[0].toUpperCase() + word.slice(1) : word)
        .join(" ");
}

export function getStatusToneClasses(value, { group = "status", variant = "badge" } = {}) {
    const tone = getTone(group, value);
    return TONES[tone]?.[variant] || TONES.gray[variant] || "";
}

export function getStatusLabel(value) {
    return formatLabel(value);
}

export function isOverdueFollowup(status, date) {
    const normalized = normalizeKey(status);
    if (normalized === "overdue" || normalized === "overdue_follow_up") return true;
    if (normalized !== "pending" || !date) return false;
    const due = new Date(date);
    return !Number.isNaN(due.getTime()) && due.getTime() < Date.now();
}

export function setStatusToneOverrides(overrides) {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides || {}));
}
